import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/seo-for-contractors-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/seo-for-contractors-colorado")({
  head: () => ({
    meta: [
      { title: "SEO for Contractors: How Colorado Trades Win Page One (2026)" },
      { name: "description", content: "SEO for contractors in Colorado: how roofers, plumbers, HVAC and electricians rank in the Map Pack and organic search — and what it should cost." },
      { property: "og:title", content: "SEO for Contractors: How Colorado Trades Win Page One" },
      { property: "og:description", content: "How Colorado contractors rank in the Map Pack and organic search — and what it should cost." },
      { property: "og:url", content: `${SITE_URL}/learn/seo-for-contractors-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "SEO for contractors in Colorado",
        description: ARTICLE?.desc ?? "How Colorado trades rank in the Map Pack and organic search.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/seo-for-contractors-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="SEO"
      read="10 min read"
      title="SEO for contractors: how Colorado trades win page one"
      intro="Contractor SEO is its own game. Your customers search in an emergency, pick from the Map Pack, and call whoever looks most established. Here's how Colorado roofers, plumbers, HVAC companies, and electricians actually win those searches — and what it should cost."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "SEO for contractors" },
      ]}
      path="/learn/seo-for-contractors-colorado"
      faqs={[
        { q: "How long does SEO take for a contractor?", a: "Expect 90 days of consistent work before the Map Pack moves meaningfully, and 4–6 months for competitive organic terms like 'roofer Colorado Springs.' Anyone promising page one in 30 days is selling, not advising." },
        { q: "Is SEO better than buying leads for contractors?", a: "For most trades, yes. Bought leads are shared, price-shopped, and stop the moment you stop paying. Rankings you own keep producing calls. Our guide on organic leads vs. lead marketplaces breaks down the math." },
        { q: "What does contractor SEO cost in Colorado?", a: "Real monthly SEO runs $950 to $2,750 depending on how many cities and services you target. A one-time website built to rank starts at $1,850. Ad spend is separate." },
      ]}
    >
      <h2>Why contractor SEO is different</h2>
      <p>
        A homeowner with a burst pipe or a leaking roof doesn't browse. They search, scan the
        map results, and call within minutes. That means contractor SEO is won or lost in two
        places: the <strong>Google Map Pack</strong> and the <strong>organic results just below
        it</strong>. Everything else is a supporting act.
      </p>

      <h2>The four things that actually move contractors up</h2>
      <ul>
        <li><strong>A fully worked Google Business Profile.</strong> Correct categories, every service listed, weekly posts and photos, and steady reviews with responses. This is the single biggest Map Pack lever.</li>
        <li><strong>A page for every service × city combination.</strong> &ldquo;Water heater repair in Monument&rdquo; beats one generic services page every time. This is the architecture we build into every contractor site.</li>
        <li><strong>Reviews that mention the job and the city.</strong> &ldquo;Fixed our furnace in Castle Rock&rdquo; is worth ten generic five-star ratings.</li>
        <li><strong>A fast, mobile-first site.</strong> Most contractor searches happen on a phone, often from a driveway. Slow sites lose the call before it happens.</li>
      </ul>

      <h2>The lead-buying trap</h2>
      <p>
        Lead platforms sell the same homeowner to three or four contractors at once, so you're
        competing on price for a customer who never heard your name. SEO compounds instead: the
        rankings and reviews you build this year still produce calls next year. We wrote the
        full comparison in{" "}
        <Link to="/learn/organic-leads-colorado-contractors">organic leads vs. lead marketplaces</Link>{" "}
        and the <Link to="/learn/angi-thumbtack-alternatives-colorado">alternatives to Angi and Thumbtack</Link>.
      </p>

      <h2>What it should cost</h2>
      <p>
        Honest Colorado numbers: a contractor website built to rank runs{" "}
        <strong>$1,850 to $5,950</strong> one-time depending on page count, and ongoing SEO
        runs <strong>$950 to $2,750/month</strong> depending on how many cities and services
        you're targeting. See the full breakdown on our <Link to="/pricing">pricing page</Link>.
      </p>

      <h2>What to do next</h2>
      <p>
        We work with trades across the Front Range — see our pages for{" "}
        <Link to="/learn/roofing-leads-colorado">roofing</Link>,{" "}
        <Link to="/learn/hvac-leads-colorado">HVAC</Link>,{" "}
        <Link to="/learn/plumbing-leads-colorado">plumbing</Link>, and{" "}
        <Link to="/learn/electrician-leads-colorado">electrical</Link>, or request a{" "}
        <Link to="/free-audit">free digital marketing audit</Link> to see exactly where you
        stand in your city.
      </p>
    </ArticleLayout>
  );
}
